import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionService } from 'src/app/shared/helpers/sesion.service';
import { SectoresService } from '../../services/sectores.service';
import { IOpcionesRequest, IOpcionItem, IComentarioSaveRequest } from '../../interfaces/sector.interface';

@Component({
  selector: 'app-comentario-form',
  templateUrl: './comentario-form.component.html'
})
export class ComentarioFormComponent implements OnInit, OnChanges {
  @Input() sector: string = '';
  @Input() descripcion: string = '';
  @Input() isEditMode: boolean = false;
  @Input() itemToEdit: any = null;
  @Output() saved = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading: boolean = false;
  tipoEstadoOptions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private sessionService: SessionService,
    private sectoresService: SectoresService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadDefaultValues();
    this.loadTipoEstadoOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sector']) {
      this.loadDefaultValues();
    }
    if (changes['itemToEdit'] && changes['itemToEdit'].currentValue) {
      this.loadAnalisis();
    }
  }

  loadAnalisis(): void {
    if (!this.itemToEdit) return;
    
    this.form.patchValue({
      sector: this.itemToEdit.sector,
      desde: this.convertDateFromDDMMYYYY(this.itemToEdit.desde),
      hasta: this.convertDateFromDDMMYYYY(this.itemToEdit.hasta),
      tipoEstado: this.itemToEdit.tipo,
      responsable: this.itemToEdit.responsable,
      fechaNota: this.convertDateFromDDMMYYYY(this.itemToEdit.fechaNota),
      fuente: this.itemToEdit.fuente,
      comentario: this.itemToEdit.comentario
    });
    // Deshabilitar campos que no se pueden editar
    this.form.get('sector')?.disable();
    this.form.get('desde')?.disable();
    this.form.get('hasta')?.disable();
    this.form.get('tipoEstado')?.disable();
    this.form.get('responsable')?.disable();
  }

  initForm(): void {
    this.form = this.fb.group({
      sector: ['', Validators.required],
      desde: ['', Validators.required],
      hasta: ['', Validators.required],
      tipoEstado: ['', Validators.required],
      responsable: ['', Validators.required],
      fechaNota: ['', Validators.required],
      fuente: ['', Validators.required],
      comentario: ['', Validators.required]
    });
  }

  loadDefaultValues(): void {
    const userSession = this.sessionService.getUserSession();
    const sUsuario = userSession && userSession.trim() !== '' ? userSession : 'admin';
    
    if (this.form && !this.isEditMode) {
      // Solo para modo nuevo: cargar valores por defecto
      this.form.patchValue({
        sector: this.sector,
        responsable: sUsuario
      });
      // Deshabilitar campos de solo lectura
      this.form.get('sector')?.disable();
      this.form.get('responsable')?.disable();
    }
  }

  convertDateFromDDMMYYYY(dateString: string): string {
    if (!dateString) return '';
    
    // Convertir de dd/mm/yyyy a yyyy-mm-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return '';
  }

  loadTipoEstadoOptions(): void {
    const request: IOpcionesRequest = {
      sTabla: 'comsec',
      sCampo: 'TipoEstado',
      iIdioma: 0
    };

    console.log('Request Opciones:', request);

    this.sectoresService.getOpciones(request).subscribe({
      next: (response) => {
        console.log('Response Opciones:', response);
        if (response.success && response.data) {
          this.tipoEstadoOptions = response.data.map(item => ({
            label: item.sOpcion,
            value: item.sValor
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar opciones:', error);
      }
    });
  }

  formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  save(): void {
    if (this.form.valid) {
      this.loading = true;
      
      const formData = this.form.getRawValue();
      const userSession = this.sessionService.getUserSession();
      const sUsuario = userSession && userSession.trim() !== '' ? userSession : 'admin';
      
      // Formatear fechas a dd/mm/aaaa
      const formatDate = (dateString: string): string => {
        if (!dateString) return '';
        
        // Crear la fecha usando el formato YYYY-MM-DD directamente
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month - 1 porque los meses van de 0-11
        
        const dayFormatted = date.getDate().toString().padStart(2, '0');
        const monthFormatted = (date.getMonth() + 1).toString().padStart(2, '0');
        const yearFormatted = date.getFullYear();
        
        return `${dayFormatted}/${monthFormatted}/${yearFormatted}`;
      };

      if (this.isEditMode) {
        // Modo edición: actualizar
        const request = {
          sUsuario: sUsuario,
          que: 0, // Actualizar
          tope: '',
          sector: this.itemToEdit.sector, // Usar el valor original
          sDesde: this.itemToEdit.desde, // Usar el valor original
          sHasta: this.itemToEdit.hasta, // Usar el valor original
          tipoEstado: this.itemToEdit.tipo, // Usar el valor original
          sFechaNota: formatDate(formData.fechaNota),
          fuente: formData.fuente,
          comentario: formData.comentario,
          odbc: 0
        };

        console.log('Request Update Comentario:', request);

        this.sectoresService.updateComentario(request).subscribe({
          next: (response) => {
            console.log('Response Update Comentario:', response);
            if (response.success && response.data) {
              this.saved.emit({ success: true, message: 'Comentario actualizado correctamente' });
            } else {
              this.saved.emit({ success: false, message: 'Error al actualizar el comentario' });
            }
            this.loading = false;
          },
          error: (error) => {
            console.error('Error al actualizar comentario:', error);
            this.saved.emit({ success: false, message: 'Error al actualizar el comentario' });
            this.loading = false;
          }
        });
      } else {
        // Modo nuevo: crear
        const request = {
          sUsuario: sUsuario,
          que: 1, // Crear
          tope: '',
          sector: formData.sector,
          sDesde: formatDate(formData.desde),
          sHasta: formatDate(formData.hasta),
          tipoEstado: formData.tipoEstado,
          sFechaNota: formatDate(formData.fechaNota),
          fuente: formData.fuente,
          comentario: formData.comentario,
          odbc: 0
        };

        console.log('Request Save Comentario:', request);

        this.sectoresService.saveComentario(request).subscribe({
          next: (response) => {
            console.log('Response Save Comentario:', response);
            if (response.success && response.data) {
              this.saved.emit({ success: true, message: 'Comentario guardado correctamente' });
            } else {
              this.saved.emit({ success: false, message: 'Error al guardar el comentario' });
            }
            this.loading = false;
          },
          error: (error) => {
            console.error('Error al guardar comentario:', error);
            this.saved.emit({ success: false, message: 'Error al guardar el comentario' });
            this.loading = false;
          }
        });
      }
    } else {
      this.saved.emit({ success: false, message: 'Por favor complete todos los campos requeridos' });
    }
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
